import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Phone, PhoneOff, Mic, MicOff, X, Minimize2, Maximize2, Loader2, PhoneIncoming, ChevronDown
} from 'lucide-react';
import { getVoiceToken, lookupCallerByPhone, getUserPhoneNumbers, CallerInfo, UserPhoneNumber } from '../../services/twilio';

interface DiallerProps {
  onClose?: () => void;
}

type CallStatus = 'idle' | 'connecting' | 'ringing' | 'in-call' | 'disconnected' | 'incoming';

const Dialler: React.FC<DiallerProps> = ({ onClose }) => {
  const [isMinimised, setIsMinimised] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [callStatus, setCallStatus] = useState<CallStatus>('idle');
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isInitialised, setIsInitialised] = useState(false);
  const [isInitialising, setIsInitialising] = useState(false);
  const [initAttempted, setInitAttempted] = useState(false);
  const [incomingFrom, setIncomingFrom] = useState<string | null>(null);
  const [callerInfo, setCallerInfo] = useState<CallerInfo | null>(null);
  const [isRegistered, setIsRegistered] = useState(false);
  const [userPhoneNumbers, setUserPhoneNumbers] = useState<UserPhoneNumber[]>([]);
  const [selectedCallerId, setSelectedCallerId] = useState<string | null>(null);
  const [callerIdOpen, setCallerIdOpen] = useState(false);

  const deviceRef = useRef<any>(null);
  const callRef = useRef<any>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const initDevice = useCallback(async () => {
    // Allow re-init if device exists but is not registered
    if (isInitialising) return;
    if (isInitialised && isRegistered) return;
    
    setIsInitialising(true);
    setInitAttempted(true);
    setError(null);

    try {
      console.log('[dialler] Getting voice token...');
      const tokenData = await getVoiceToken();
      console.log('[dialler] Token result:', tokenData ? 'received' : 'null');
      if (!tokenData) {
        console.log('[dialler] Voice token not available - Twilio not configured');
        setIsInitialising(false);
        return;
      }
      console.log('[dialler] Token identity:', tokenData.identity);

      const { Device } = await import('@twilio/voice-sdk');
      
      const device = new Device(tokenData.token, {
        logLevel: 1,
        codecPreferences: ['opus', 'pcmu'] as any,
      });

      device.on('registered', () => {
        console.log('[dialler] ✅ Device REGISTERED with identity:', tokenData.identity);
        console.log('[dialler] ✅ Waiting for incoming calls to:', tokenData.identity);
        setIsInitialised(true);
        setIsInitialising(false);
        setIsRegistered(true);
      });

      device.on('unregistered', () => {
        console.log('[dialler] ⚠️ Device UNREGISTERED - will not receive calls');
        setIsRegistered(false);
        // Try to re-register after a short delay
        setTimeout(() => {
          if (deviceRef.current) {
            console.log('[dialler] Attempting to re-register...');
            deviceRef.current.register().catch((e: any) => {
              console.error('[dialler] Re-register failed:', e);
            });
          }
        }, 2000);
      });

      device.on('registering', () => {
        console.log('[dialler] 🔄 Device registering...');
      });

      device.on('error', (err: any) => {
        console.error('[dialler] ❌ Device error:', err);
        setError(err.message || 'Device error');
        setCallStatus('idle');
        setIsInitialising(false);
      });

      device.on('incoming', async (call: any) => {
        const fromNumber = call.parameters?.From || 'Unknown';
        console.log('[dialler] Incoming call from:', fromNumber);
        
        setCallStatus('incoming');
        setIncomingFrom(fromNumber);
        setIsMinimised(false);
        callRef.current = call;

        // Look up caller info
        if (fromNumber && fromNumber !== 'Unknown') {
          const info = await lookupCallerByPhone(fromNumber);
          if (info) {
            console.log('[dialler] Found caller:', info);
            setCallerInfo(info);
          }
        }

        call.on('accept', () => {
          console.log('[dialler] Incoming call accepted');
          setCallStatus('in-call');
          setCallDuration(0);
          timerRef.current = setInterval(() => {
            setCallDuration(d => d + 1);
          }, 1000);
        });

        call.on('disconnect', () => {
          console.log('[dialler] Incoming call disconnected');
          handleHangup();
        });

        call.on('cancel', () => {
          console.log('[dialler] Incoming call cancelled');
          handleHangup();
        });
      });

      console.log('[dialler] Registering device...');
      await device.register();
      deviceRef.current = device;
      console.log('[dialler] Device registration initiated');
    } catch (err: any) {
      console.error('[dialler] Init failed:', err);
      setError(err?.message || 'Device initialization failed');
      setIsInitialising(false);
    }
  }, [isInitialised, isInitialising, isRegistered]);

  // Initialize device on first expand
  useEffect(() => {
    if (!isMinimised && !initAttempted) {
      initDevice();
    }
  }, [isMinimised, initAttempted, initDevice]);

  // Fetch user's assigned phone numbers on first expand
  useEffect(() => {
    if (!isMinimised && userPhoneNumbers.length === 0) {
      getUserPhoneNumbers().then((numbers) => {
        if (numbers.length > 0) {
          setUserPhoneNumbers(numbers);
          const defaultNum = numbers.find(n => n.is_default) || numbers[0];
          setSelectedCallerId(defaultNum.phone_number);
        }
      });
    }
  }, [isMinimised]);

  // Auto-refresh token every 50 minutes (tokens expire at 60 min)
  useEffect(() => {
    const refreshInterval = setInterval(async () => {
      if (deviceRef.current && isInitialised) {
        console.log('[dialler] Refreshing voice token...');
        try {
          const tokenData = await getVoiceToken();
          if (tokenData?.token) {
            deviceRef.current.updateToken(tokenData.token);
            console.log('[dialler] Token refreshed');
          }
        } catch (err) {
          console.error('[dialler] Token refresh failed:', err);
        }
      }
    }, 50 * 60 * 1000); // 50 minutes

    return () => clearInterval(refreshInterval);
  }, [isInitialised]);

  useEffect(() => {
    const handleInitiateCall = (e: CustomEvent) => {
      if (e.detail?.number) {
        setPhoneNumber(e.detail.number);
        setIsMinimised(false);
      }
    };

    window.addEventListener('initiate-call', handleInitiateCall as EventListener);

    return () => {
      window.removeEventListener('initiate-call', handleInitiateCall as EventListener);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (deviceRef.current) {
        deviceRef.current.destroy();
      }
    };
  }, []);

  const handleCall = async () => {
    if (!phoneNumber.trim() || !deviceRef.current) return;

    setError(null);
    setCallStatus('connecting');

    try {
      const connectParams: Record<string, string> = { To: phoneNumber };
      if (selectedCallerId) {
        connectParams.CallerId = selectedCallerId;
      }

      const call = await deviceRef.current.connect({
        params: connectParams,
      });

      callRef.current = call;

      call.on('accept', () => {
        console.log('[dialler] Call accepted');
        setCallStatus('in-call');
        setCallDuration(0);
        timerRef.current = setInterval(() => {
          setCallDuration(d => d + 1);
        }, 1000);
      });

      call.on('disconnect', () => {
        console.log('[dialler] Call disconnected');
        handleHangup();
      });

      call.on('cancel', () => {
        console.log('[dialler] Call cancelled');
        handleHangup();
      });

      call.on('reject', () => {
        console.log('[dialler] Call rejected');
        handleHangup();
      });

      call.on('error', (err: any) => {
        console.error('[dialler] Call error:', err);
        setError(err.message || 'Call failed');
        handleHangup();
      });

      setCallStatus('ringing');
    } catch (err) {
      console.error('[dialler] Connect failed:', err);
      setError('Failed to connect call');
      setCallStatus('idle');
    }
  };

  const handleHangup = () => {
    if (callRef.current) {
      callRef.current.disconnect();
      callRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setCallStatus('idle');
    setIsMuted(false);
    setCallDuration(0);
    setIncomingFrom(null);
    setCallerInfo(null);
  };

  const handleAnswer = () => {
    if (callRef.current) {
      callRef.current.accept();
    }
  };

  const handleReject = () => {
    if (callRef.current) {
      callRef.current.reject();
      callRef.current = null;
    }
    setCallStatus('idle');
    setIncomingFrom(null);
  };

  const handleMute = () => {
    if (callRef.current) {
      callRef.current.mute(!isMuted);
      setIsMuted(!isMuted);
    }
  };

  const handleKeyPress = (digit: string) => {
    setPhoneNumber(prev => prev + digit);
    if (callRef.current && callStatus === 'in-call') {
      callRef.current.sendDigits(digit);
    }
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusText = (): string => {
    switch (callStatus) {
      case 'connecting': return 'Connecting...';
      case 'ringing': return 'Ringing...';
      case 'incoming': return 'Incoming Call';
      case 'in-call': return formatDuration(callDuration);
      case 'disconnected': return 'Disconnected';
      default: 
        if (!isInitialised) return 'Initialising...';
        if (!isRegistered) return 'Reconnecting...';
        return 'Ready';
    }
  };

  const getStatusColor = (): string => {
    switch (callStatus) {
      case 'in-call': return 'text-green-400';
      case 'incoming': return 'text-green-400 animate-pulse';
      case 'connecting':
      case 'ringing': return 'text-yellow-400';
      default: return 'text-slate-400';
    }
  };

  if (isMinimised) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={() => setIsMinimised(false)}
          className={`w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all ${
            callStatus === 'incoming'
              ? 'bg-green-600 animate-pulse ring-4 ring-green-400 ring-opacity-50'
              : callStatus === 'in-call' 
              ? 'bg-green-600 animate-pulse' 
              : 'bg-slate-800 hover:bg-slate-700'
          }`}
        >
          {callStatus === 'incoming' ? (
            <PhoneIncoming size={24} className="text-white" />
          ) : (
            <Phone size={24} className={callStatus === 'in-call' ? 'text-white' : 'text-orange-500'} />
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 w-72 bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-800 border-b border-slate-700">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Phone size={18} className="text-orange-500" />
            <span 
              className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ${
                isRegistered ? 'bg-green-500' : 'bg-yellow-500 animate-pulse'
              }`}
              title={isRegistered ? 'Connected' : 'Connecting...'}
            />
          </div>
          <span className="text-sm font-medium text-white">Dialler</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsMinimised(true)}
            className="p-1.5 text-slate-400 hover:text-white transition-colors"
          >
            <Minimize2 size={16} />
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1.5 text-slate-400 hover:text-white transition-colors"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Status */}
      <div className="px-4 py-3 text-center border-b border-slate-800">
        <div className={`text-lg font-semibold ${getStatusColor()}`}>
          {getStatusText()}
        </div>
        {phoneNumber && callStatus !== 'idle' && (
          <div className="text-sm text-slate-400 mt-1">{phoneNumber}</div>
        )}
      </div>

      {/* Initialising - only show briefly */}
      {isInitialising && (
        <div className="px-4 py-2 text-center">
          <Loader2 className="w-5 h-5 animate-spin text-orange-500 mx-auto" />
        </div>
      )}

      {/* Phone Number Input */}
      {!isInitialising && callStatus === 'idle' && (
        <div className="p-4">
          {/* Caller ID selector */}
          {userPhoneNumbers.length > 1 && (
            <div className="relative mb-3">
              <button
                onClick={() => setCallerIdOpen(!callerIdOpen)}
                className="w-full flex items-center justify-between px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-300 hover:border-slate-600 transition-colors"
              >
                <span className="text-slate-500 text-xs mr-2">From:</span>
                <span className="flex-1 text-left truncate">
                  {userPhoneNumbers.find(n => n.phone_number === selectedCallerId)?.friendly_name || selectedCallerId}
                </span>
                <ChevronDown size={14} className={`text-slate-500 ml-1 transition-transform ${callerIdOpen ? 'rotate-180' : ''}`} />
              </button>
              {callerIdOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-slate-800 border border-slate-700 rounded-lg overflow-hidden z-10 shadow-xl">
                  {userPhoneNumbers.map((num) => (
                    <button
                      key={num.phone_number}
                      onClick={() => {
                        setSelectedCallerId(num.phone_number);
                        setCallerIdOpen(false);
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                        num.phone_number === selectedCallerId
                          ? 'bg-orange-500/10 text-orange-400'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <span className="flex-1 truncate">{num.friendly_name || num.phone_number}</span>
                      <span className="text-xs text-slate-500">{num.phone_number}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <input
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="+44 7700 900000"
            className="w-full px-4 py-3 text-center text-lg bg-slate-800 border border-slate-700 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
          />

          {/* Dial Pad */}
          <div className="grid grid-cols-3 gap-2 mt-3">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map((digit) => (
              <button
                key={digit}
                onClick={() => handleKeyPress(digit)}
                className="w-full py-3 text-lg font-medium bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors"
              >
                {digit}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Call Controls */}
      <div className="p-4 border-t border-slate-800">
        {callStatus === 'incoming' ? (
          <div className="space-y-3">
            <div className="text-center">
              <PhoneIncoming size={32} className="text-green-400 mx-auto mb-2 animate-bounce" />
              {callerInfo ? (
                <>
                  <p className="text-white font-medium text-lg">{callerInfo.name}</p>
                  {callerInfo.company && (
                    <p className="text-slate-400 text-sm">{callerInfo.company}</p>
                  )}
                  <p className="text-slate-500 text-xs mt-1">{incomingFrom}</p>
                </>
              ) : (
                <p className="text-white font-medium">{incomingFrom || 'Unknown'}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleReject}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-red-600 hover:bg-red-500 text-white font-medium rounded-xl transition-colors"
              >
                <PhoneOff size={20} />
                Decline
              </button>
              <button
                onClick={handleAnswer}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-green-600 hover:bg-green-500 text-white font-medium rounded-xl transition-colors"
              >
                <Phone size={20} />
                Answer
              </button>
            </div>
          </div>
        ) : callStatus === 'idle' ? (
          <button
            onClick={handleCall}
            disabled={!phoneNumber.trim() || !isInitialised}
            className="w-full flex items-center justify-center gap-2 py-3 bg-green-600 hover:bg-green-500 disabled:bg-slate-700 disabled:text-slate-500 text-white font-medium rounded-xl transition-colors"
          >
            <Phone size={20} />
            Call
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={handleMute}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl transition-colors ${
                isMuted 
                  ? 'bg-yellow-600 hover:bg-yellow-500 text-white' 
                  : 'bg-slate-800 hover:bg-slate-700 text-slate-300'
              }`}
            >
              {isMuted ? <MicOff size={20} /> : <Mic size={20} />}
            </button>
            <button
              onClick={handleHangup}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-red-600 hover:bg-red-500 text-white font-medium rounded-xl transition-colors"
            >
              <PhoneOff size={20} />
              End
            </button>
          </div>
        )}
      </div>

      {/* Not initialised message */}
      {!isInitialised && !isInitialising && (
        <div className="px-4 pb-4 text-center">
          <p className="text-xs text-slate-500">
            {error ? error : 'Configure Twilio Voice credentials to enable calling'}
          </p>
        </div>
      )}
    </div>
  );
};

export default Dialler;
